import React, { useState } from "react";

// ---------------------------------------------------------------------------
// McpConnectTabs
// ---------------------------------------------------------------------------
// Single source of truth for "connect Limelite Deploy to your AI tool".
// Renders a tabbed picker (Claude Code, Cursor, Windsurf, Cline/RooCode, Other)
// where each tab shows the exact command/JSON config for that client plus a
// one-click copy button. Used on the public setup page AND inside the
// authenticated dashboard/account so logged-in users can copy a config that
// already has their real API key filled in.
//
// Props:
//   - apiKey:  string | null  → when present, replaces the YOUR_KEY placeholder
//   - mcpUrl:  string         → override the hosted MCP endpoint (defaults to
//                               the current origin + /mcp, falling back to prod)
//   - className: string       → optional extra wrapper classes
// ---------------------------------------------------------------------------

// Default MCP endpoint. On the client we prefer the live origin so the snippet
// is always correct regardless of which domain the app is served from.
const DEFAULT_MCP_URL =
  typeof window !== "undefined" ? `${window.location.origin}/mcp` : "https://rivieratech.solutions/mcp";

// The placeholder token we swap for the user's real key when we have it.
const KEY_PLACEHOLDER = "YOUR_KEY";

// Build the per-client connect configs for a given MCP URL. Kept as a function
// (not a module constant) so the URL stays in sync with the running origin.
function buildClients(mcpUrl) {
  return [
    {
      id: "claude",
      label: "Claude Code",
      command: `claude mcp add limelite-deploy ${mcpUrl} -t http -H "Authorization: Bearer ${KEY_PLACEHOLDER}"`,
      steps: [
        "Open your terminal",
        `Run the command above (replace ${KEY_PLACEHOLDER} with your API key)`,
        "Start Claude Code — the tools are ready",
      ],
      tip: 'Say "deploy my project from github.com/you/repo" and it handles the rest.',
    },
    {
      id: "cursor",
      label: "Cursor",
      command: `{
  "mcpServers": {
    "limelite-deploy": {
      "url": "${mcpUrl}",
      "headers": {
        "Authorization": "Bearer ${KEY_PLACEHOLDER}"
      }
    }
  }
}`,
      isJson: true,
      steps: [
        "Open Cursor → Settings (⌘,) → search for MCP",
        "Click 'Edit in settings.json' or open ~/.cursor/mcp.json",
        `Paste the config above (replace ${KEY_PLACEHOLDER})`,
        "Restart Cursor",
      ],
      tip: 'In Cursor\'s Composer, say "deploy my app" to get started.',
    },
    {
      id: "windsurf",
      label: "Windsurf",
      command: `{
  "mcpServers": {
    "limelite-deploy": {
      "serverUrl": "${mcpUrl}",
      "headers": {
        "Authorization": "Bearer ${KEY_PLACEHOLDER}"
      }
    }
  }
}`,
      isJson: true,
      steps: [
        "Open Windsurf → Cascade panel",
        "Click the MCP plugin icon → Add MCP Server",
        `Paste the config above (replace ${KEY_PLACEHOLDER})`,
        "Click Save — tools load immediately",
      ],
      tip: 'Ask Cascade: "deploy this project and give me the live URL."',
    },
    {
      id: "cline",
      label: "Cline / RooCode",
      command: `{
  "mcpServers": {
    "limelite-deploy": {
      "transport": "http",
      "url": "${mcpUrl}",
      "headers": {
        "Authorization": "Bearer ${KEY_PLACEHOLDER}"
      }
    }
  }
}`,
      isJson: true,
      steps: [
        "Open VS Code → Cline extension sidebar",
        "Click MCP Servers → Add Server → Manual config",
        `Paste the config above (replace ${KEY_PLACEHOLDER})`,
      ],
      tip: "Works with Cline and RooCode. Both support HTTP MCP transport.",
    },
    {
      id: "other",
      label: "Other",
      command: `MCP URL:   ${mcpUrl}
Transport: HTTP / SSE
Auth:      Authorization: Bearer ${KEY_PLACEHOLDER}`,
      steps: [
        "Add a new MCP server in your tool's settings",
        `Set the URL to: ${mcpUrl}`,
        `Set the Authorization header to: Bearer ${KEY_PLACEHOLDER}`,
      ],
      tip: "Any tool that supports MCP over HTTP works — Droid, Zed, Continue, and more.",
    },
  ];
}

// Small inline copy-to-clipboard button with a transient "Copied!" state.
function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      // Modern path first.
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for non-secure contexts / older browsers.
      try {
        const el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      } catch {
        return;
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button
      type="button"
      onClick={copy}
      className="text-xs border border-gray-700 hover:border-gray-500 text-silver-dark hover:text-silver-light rounded px-3 py-1 transition-colors flex-shrink-0"
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

export default function McpConnectTabs({ apiKey = null, mcpUrl = DEFAULT_MCP_URL, className = "" }) {
  // Debug: trace renders so we can spot unnecessary re-renders later.
  console.log(
    `[${new Date().toISOString()}] [McpConnectTabs] render hasKey=${Boolean(apiKey)} mcpUrl=${mcpUrl}`
  );

  const [activeIde, setActiveIde] = useState("claude");

  // Build configs for this URL once per render of the URL.
  const clients = buildClients(mcpUrl);
  const rawIde = clients.find((i) => i.id === activeIde) || clients[0];

  // When we have the real key, swap the placeholder everywhere and soften the
  // "replace YOUR_KEY" instruction since it is already filled in.
  const ide = apiKey
    ? {
        ...rawIde,
        command: rawIde.command.split(KEY_PLACEHOLDER).join(apiKey),
        steps: rawIde.steps.map((s) =>
          s.includes(KEY_PLACEHOLDER)
            ? s.replace(`(replace ${KEY_PLACEHOLDER} with your API key)`, "(your key is already filled in)").replace(`(replace ${KEY_PLACEHOLDER})`, "(your key is already filled in)")
            : s
        ),
      }
    : rawIde;

  return (
    <div className={className}>
      <div className="bg-off-black border border-gray-800 rounded-2xl overflow-hidden">
        {/* Tab strip — one tab per supported client. */}
        <div className="flex border-b border-gray-800 overflow-x-auto">
          {clients.map((i) => (
            <button
              key={i.id}
              type="button"
              onClick={() => setActiveIde(i.id)}
              className={`flex-shrink-0 px-4 py-2.5 text-sm transition-colors ${
                activeIde === i.id
                  ? "text-lime-400 border-b-2 border-lime-500 bg-gray-900/40"
                  : "text-silver-dark hover:text-silver-medium"
              }`}
            >
              {i.label}
            </button>
          ))}
        </div>

        {/* Active client's command + steps. */}
        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-4">
            <pre className="text-sm text-silver-light font-mono whitespace-pre-wrap break-all flex-1 leading-relaxed">
              {ide.command}
            </pre>
            <CopyButton text={ide.command} />
          </div>
          <ol className="text-sm text-silver-dark space-y-1.5 list-decimal list-inside">
            {ide.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        </div>
      </div>

      {/* Per-client tip. */}
      {ide.tip && (
        <div className="bg-gray-900/40 border border-gray-800 rounded-lg px-4 py-3 mt-3">
          <p className="text-xs text-silver-dark">
            <span className="text-lime-500 font-medium">Tip — </span>
            {ide.tip}
          </p>
        </div>
      )}
    </div>
  );
}
